import { useCallback, useMemo } from 'react';
import { Dialog as MUIDialog, DialogProps as MUIDialogProps } from '@mui/material';
import { boxShadow, S6 } from '../../theme';
import Backdrop from '../Backdrop';
import { mergeSX } from '../../services/helpers';

export { default as DialogTitle } from './components/DialogTitle';
export { default as DialogContent } from './components/DialogContent';
export { default as DialogActions } from './components/DialogActions';

const buildSX = () => ({
  '& .MuiDialog-paper': {
    boxShadow,
    borderRadius: '16px',
    color: S6,
  },
  '& form': {
    overflowY: 'inherit',
    display: 'inherit',
    flexDirection: 'inherit',
  },
});

export interface IDialogProps extends MUIDialogProps {
  clickOutsideToClose?: boolean;
  onClose?: () => void;
}

const Dialog = (props: IDialogProps) => {
  const {
    children,
    slots = {},
    onClose,
    clickOutsideToClose = true,
    sx,
    /*
      in the development mode - set this to 'true' so 'autoFocus' in the 'TextField' works with the
        'StrictMode' enabled
      in the production mode - there will be no 'StrictMode' so this property is not needed
      for more information: https://github.com/mui/material-ui/issues/33004#issuecomment-1473299089
    */
    disableRestoreFocus = process.env.NODE_ENV === 'production' ? undefined : true,
    ...restProps
  } = props;

  const { backdrop, root } = slots;
  const processedSlots = useMemo(() => ({
    backdrop: backdrop ?? Backdrop,
    root,
  }), [backdrop, root]);

  const handleClose = useCallback(() => {
    if (!clickOutsideToClose || !onClose) return;
    onClose();
  }, [onClose, clickOutsideToClose]);

  return (
    <MUIDialog
      slots={processedSlots}
      onClose={handleClose}
      sx={mergeSX(buildSX(), sx)}
      disableRestoreFocus={disableRestoreFocus}
      {...restProps}
    >
      {children}
    </MUIDialog>
  );
};

export default Dialog;
